import { useCallback, useRef } from 'react';
import { moveEntriesToDestination, destinationForDropTarget } from '../../utils/collectionBulkMove';
import { EMPTY_MARKED } from './sidebarState';
import { computeDropIndicator, applySidebarDrop } from './sidebarDrop';

/** Start / move / end / cancel of a tree drag. */
export function useSidebarDragHandlers({
  collections,
  onCollectionsReorder,
  setActiveDragId,
  setDropIndicator,
}) {
  // ── Drag handlers ──────────────────────────────────────────────────────────
  // The tree is NOT reordered during the drag — only a lightweight insertion
  // indicator (line / container highlight) follows the pointer. The next tree is
  // computed once, on drop, via applySidebarDrop.

  const handleDragStart = useCallback((activeId) => {
    // SNAPSHOT THE SELECTION HERE, not at drop time. A drag also produces a
    // click on the row it started from, and that click runs `markOnly` — so by
    // the time the drop lands the set has already collapsed to one row and the
    // other marked rows are silently left behind. Caught by the Playwright spec;
    // jsdom never fired the click, so the unit tests could not see it.
    dragSelectionRef.current =
      markedIdsRef.current.size > 1 && markedIdsRef.current.has(activeId)
        ? new Set(markedIdsRef.current)
        : null;
    setDropIndicator(null);
    setActiveDragId(activeId);
  }, [setActiveDragId, setDropIndicator]);

  const handleDragMove = useCallback((activeId, overId, position) => {
    if (!overId) {
      setDropIndicator(null);
      return;
    }
    setDropIndicator(computeDropIndicator(collections, activeId, overId, position));
  }, [collections, setDropIndicator]);

  // Read through a ref, not the value: `handleDragEnd` is declared above
  // `useTreeMultiSelect` (a direct reference is a temporal-dead-zone error), and
  // the drag hook holds this callback — adding `markedIds` to its deps would
  // change its identity on every marking click.
  const markedIdsRef = useRef(EMPTY_MARKED);
  // The marked set as it was when the drag STARTED — see handleDragStart.
  const dragSelectionRef = useRef(null);

  const handleDragEnd = useCallback((activeId, overId, position) => {
    let next = null;
    if (overId && overId !== activeId) {
      // DRAGGING ONE OF A SELECTION DRAGS THE WHOLE SELECTION — the same rows
      // "Move to" would act on, and the same mover, so the two routes cannot
      // drift apart. Dropping ONTO a marked row is a no-op rather than a move
      // into itself.
      const dragSelection = dragSelectionRef.current;
      const bulk = !!dragSelection && dragSelection.has(activeId);
      if (bulk && !dragSelection.has(overId)) {
        const dest = destinationForDropTarget(collections, overId, position);
        if (dest) next = moveEntriesToDestination(collections, [...dragSelection], dest);
      } else if (!bulk) {
        next = applySidebarDrop(collections, activeId, overId, position);
      }
    }
    if (next && onCollectionsReorder) onCollectionsReorder(next);

    dragSelectionRef.current = null;
    setActiveDragId(null);
    setDropIndicator(null);
  }, [collections, onCollectionsReorder, setActiveDragId, setDropIndicator]);

  const handleDragCancel = useCallback(() => {
    dragSelectionRef.current = null;
    setActiveDragId(null);
    setDropIndicator(null);
  }, [setActiveDragId, setDropIndicator]);

  return { handleDragStart, handleDragMove, markedIdsRef, handleDragEnd, handleDragCancel };
}
